/**
 * Job Detail API
 *
 * API client functions for reading and updating a single job's details.
 *
 * Endpoints:
 *   GET   /jobs/job-detail/:jobId
 *   PATCH /jobs/job-detail/:jobId
 * Backend  : apps/backend/src/domains/works-lite/jobs/job-detail/routes.ts
 */

import { apiClient } from '@/lib/api';
import type {
  AddJobTaskBody,
  ConversationsResponse,
  JobDetail,
  JobDetailTask,
  UpdateJobDetailBody,
} from '@skewb-works/contracts';

export type { JobDetail, UpdateJobDetailBody, ConversationsResponse };

/**
 * Fetch details for a single job.
 */
export async function getJobDetail(jobId: number): Promise<JobDetail> {
  return apiClient.get<JobDetail>(`/jobs/job-detail/${jobId}`);
}

/**
 * Update editable fields on a job. Currently only `duration` (hours).
 */
export async function updateJobDetail(
  jobId: number,
  body: UpdateJobDetailBody,
): Promise<JobDetail> {
  return apiClient.patch<JobDetail>(`/jobs/job-detail/${jobId}`, body);
}

export async function getJobConversations(jobId: number): Promise<ConversationsResponse> {
  return apiClient.get<ConversationsResponse>(`/jobs/job-detail/${jobId}/conversations`);
}

export async function addJobTask(jobId: number, body: AddJobTaskBody): Promise<JobDetailTask> {
  return apiClient.post<JobDetailTask>(`/jobs/job-detail/${jobId}/tasks`, body);
}

export async function removeJobTask(jobId: number, taskId: number): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(`/jobs/job-detail/${jobId}/tasks/${taskId}`);
}

export interface AddressSearchResult {
  id: string;
  type: string;
  text: string;
  description: string;
}

export interface AddressDetail {
  line1: string;
  line2: string;
  city: string;
  county: string;
  postcode: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
}

export async function searchAddresses(
  text: string,
  container?: string,
): Promise<AddressSearchResult[]> {
  const params = new URLSearchParams({ text });
  if (container) params.set('container', container);
  return apiClient.get<AddressSearchResult[]>(`/jobs/job-detail/address/search?${params}`);
}

export async function getAddressDetail(id: string): Promise<AddressDetail> {
  return apiClient.get<AddressDetail>(
    `/jobs/job-detail/address/detail?id=${encodeURIComponent(id)}`,
  );
}
